import { apiClient } from './client';
import { User } from '../models/User';
import { Transaction } from '../models/Transaction';

export interface UserCredentialsUpdate {
  pushStatus?: number;
  emailStatus?: number;
  // Add other credential fields as needed
}

export interface UserCredentialsUpdateResponse {
  success?: boolean;
  error?: string;
}

export interface FavoritesResponse {
  success?: boolean;
  error?: string;
  message?: string;
}

export interface FavoritesListResponse {
  users: User[];
  totalCount: number;
  error?: string;
}

export interface MatchesListResponse {
  users: User[];
  totalCount: number;
  error?: string;
}

export interface MatchCheckResponse {
  isMatch: boolean;
  userId: number;
  error?: string;
}

export interface UpgradeAccountResponse {
  success: boolean;
  premiumExpiry?: string;
  error?: string;
}

export interface LastTransactionResponse {
  success: boolean;
  lastTransaction: Transaction | null;
}

export interface VisitorEntry {
  date: string;
  viewedByUser: User;
}

export interface VisitorsListResponse {
  visitors: VisitorEntry[];
  totalCount: number;
  error?: string;
}

/**
 * Update user credentials (pushStatus, emailStatus, etc.)
 */
export const updateUserCredentials = async (updates: UserCredentialsUpdate): Promise<boolean> => {
  const formData = new FormData();
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) formData.append(`UserCredentials[${key}]`, String(value));
  });
  const result = await apiClient.postForm<UserCredentialsUpdateResponse>('/user/area/update?json=1', formData);
  if (!result.ok) {
    console.error('Failed to update user credentials:', result.error);
    return false;
  }
  return !result.data?.error;
};

/**
 * Add a user to favorites
 */
export const addToFavorites = async (userId: number): Promise<FavoritesResponse> => {
  const result = await apiClient.get<FavoritesResponse>('/user/favorite/add', { id: userId, json: 1 });
  if (!result.ok) {
    console.error('Failed to add user to favorites:', result.error);
    return { error: 'Failed to add user to favorites' };
  }
  return result.data || { success: false };
};

/**
 * Remove a user from favorites
 */
export const removeFromFavorites = async (userId: number): Promise<FavoritesResponse> => {
  const result = await apiClient.delete<FavoritesResponse>('/user/api/favorites', { id: userId });
  if (!result.ok) {
    console.error('Failed to remove user from favorites:', result.error);
    return { error: 'Failed to remove user from favorites' };
  }
  return result.data || { success: false };
};

/**
 * Get the user's favorites list with optional search
 */
export const getFavorites = async (search: string = ''): Promise<FavoritesListResponse> => {
  const result = await apiClient.get<FavoritesListResponse>('/user/api/favorites', {
    search: search || undefined,
    'per-page': 999999,
    page: 1
  });
  if (!result.ok) {
    console.error('Failed to fetch favorites:', result.error);
    return { users: [], totalCount: 0, error: 'Failed to fetch favorites' };
  }
  const data = result.data || { users: [], totalCount: 0 };
  if (data.error) {
    return { users: [], totalCount: 0, error: data.error };
  }
  return { users: data.users || [], totalCount: data.totalCount || 0 };
};

/**
 * Get the count of user's favorites
 */
export const getFavoritesCount = async (): Promise<number> => {
  const result = await apiClient.get<{ count?: number }>('/user/favorite/count', { json: 1 });
  if (!result.ok) {
    console.error('Failed to fetch favorites count:', result.error);
    return 0;
  }
  return result.data?.count || 0;
};

/**
 * Get the user's matches list with optional search
 */
export const getMatches = async (search: string = ''): Promise<MatchesListResponse> => {
  const result = await apiClient.get<MatchesListResponse>('/user/slider/matches', {
    search: search || undefined
  });
  if (!result.ok) {
    console.error('Failed to fetch matches:', result.error);
    return { users: [], totalCount: 0, error: 'Failed to fetch matches' };
  }
  const data = result.data || { users: [], totalCount: 0 };
  if (data.error) return { users: [], totalCount: 0, error: data.error };
  return { users: data.users || [], totalCount: data.totalCount || 0 };
};

/**
 * Check if a specific user is a match
 */
export const checkUserMatch = async (userId: number): Promise<MatchCheckResponse> => {
  const result = await apiClient.get<MatchCheckResponse>('/user/slider/matches', { userId });
  if (!result.ok) {
    console.error('Failed to check user match:', result.error);
    return { isMatch: false, userId, error: 'Failed to check user match' };
  }
  const data = result.data!;
  if (data.error) return { isMatch: false, userId, error: data.error };
  return { isMatch: data.isMatch || false, userId: data.userId || userId };
};

/**
 * Upgrade user account to premium
 */
export async function upgradeAccount(name: string): Promise<UpgradeAccountResponse> {
  const result = await apiClient.postJson<UpgradeAccountResponse>(`/user/upgrade/do?name=${encodeURIComponent(name)}`);
  if (!result.ok) {
    console.error('Failed to upgrade account:', result.error);
    return { success: false, error: 'Failed to upgrade account' };
  }
  return result.data || { success: false };
}

/**
 * Get the user's last transaction
 */
export const getLastTransaction = async (): Promise<LastTransactionResponse> => {
  const result = await apiClient.get<LastTransactionResponse>('/user/api/last-transaction');
  if (!result.ok) {
    console.error('Failed to fetch last transaction:', result.error);
    return { success: false, lastTransaction: null };
  }
  return result.data || { success: false, lastTransaction: null };
};

/**
 * Get the user's profile visitors list
 */
export const getVisitors = async (): Promise<VisitorsListResponse> => {
  const result = await apiClient.get<VisitorsListResponse>('/user/api/visitors');
  if (!result.ok) {
    console.error('Failed to fetch visitors:', result.error);
    return { visitors: [], totalCount: 0, error: 'Failed to fetch visitors' };
  }
  const data = result.data || { visitors: [], totalCount: 0 };
  if (data.error) {
    return { visitors: [], totalCount: 0, error: data.error };
  }
  return { visitors: data.visitors || [], totalCount: data.totalCount || 0 };
};